import Check from '@mui/icons-material/Check'
import Delete from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'

import MuiButton from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import FormCreator from '@/shared/FormCreator'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import * as Yup from 'yup'

const inputConfig = {
  variant: 'standard',
  inputBaseProps: {
    labelSuffix: '',
    flexDirection: 'column',
    spacing: undefined,
    labelProps: { sx: { color: theme => theme.palette.btn_positive.main } },
  },
  color: 'btn_positive',
  endAdornment: args => {
    const {
      value,
      inputProps: { maxLength },
    } = args
    return (
      <Typography
        variant="caption"
        sx={{ color: '#00000040', fontSize: '14px !important' }}
      >{`${value.length}/${maxLength}`}</Typography>
    )
  },
}

const CreateRegisteredUserCard = forwardRef(
  ({ dispatch, index, id, onRemove, showRemove, loading }, ref) => {
    const form = useRef()
    const [success, setSuccess] = useState(false)
    const { t } = useTranslation()

    const onSubmit = async (values, resolve) => {
      await dispatch({
        type: 'user/createUser',
        payload: values,
        successCallback: data => {
          setSuccess(true)
          resolve(data)
        },
        errorCallback: message => {
          form.current.setError('email', { type: 'custom', message })
          resolve(false)
        },
      })
    }

    useImperativeHandle(ref, () => ({
      form: form.current,
      submit: async () => {
        if (!success) {
          return new Promise(resolve => {
            form.current.handleSubmit(values => {
              onSubmit(values, resolve)
            })()
          })
        }

        return Promise.resolve(true)
      },
    }))

    return (
      <Box
        sx={{
          mb: 2,
        }}
      >
        {showRemove && (
          <Stack direction="row" justifyContent="flex-end">
            <IconButton
              disabled={loading}
              sx={{ color: theme => theme.palette.primary[20].main }}
              onClick={() => {
                onRemove?.(id)
              }}
            >
              <Delete />
            </IconButton>
          </Stack>
        )}
        <FormCreator
          ref={form}
          isCreateRegisteredUserCard
          formItems={[
            // {
            //   label: t('general.user_group_name'),
            //   name: 'userGroup',
            //   defaultValue: '',
            //   inputConfig: { ...inputConfig, endAdornment: null },
            //   disabled: true,
            // },
            {
              label: t('general.company_name'),
              name: 'companyName',
              defaultValue: '',
              inputConfig: { ...inputConfig, inputProps: { maxLength: 20 } },
            },
            {
              label: `*${t('general.email')}`,
              name: 'email',
              validator: Yup.string()
                .required(t('validation.email_required'))
                .email(t('validation.email_format'))
                .duplicateEmail(t('validation.email_duplicate'), index),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
                inputProps: { maxLength: 40 },
                inputBaseProps: {
                  ...inputConfig.inputBaseProps,
                  container: false,
                  labelSuffix: (
                    <Typography sx={{ color: theme => theme.palette.primary.main, ml: 1 }}>{`(${t(
                      'general.required',
                    )})`}</Typography>
                  ),
                },
              },
            },
            {
              label: t('general.phone'),
              name: 'phone',
              defaultValue: '',
              validator: Yup.string()
                .matches(/^(09\d{8})?$/, t('validation.phone_ten_digits_start_zero_nine'))
                .test('check-starting-digits', 'Phone number must start with "09"', value => {
                  if (!value) return true
                  return value.startsWith('09')
                })
                .test('check-number-length', 'Phone number must have exactly 10 digits', value => {
                  if (!value) return true
                  return value.replace(/[^0-9]/g, '').length === 10
                }),
              inputConfig: { ...inputConfig, inputProps: { maxLength: 10 } },
            },
            {
              label: t('general.last_name'),
              name: 'lastName',
              defaultValue: '',
              inputConfig: { ...inputConfig, inputProps: { maxLength: 10 } },
            },
            {
              label: t('general.first_name'),
              name: 'firstName',
              defaultValue: '',
              inputConfig: { ...inputConfig, inputProps: { maxLength: 10 } },
            },
            {
              label: t('general.name'),
              name: 'cname',
              defaultValue: '',
              inputConfig: { ...inputConfig, inputProps: { maxLength: 10 } },
            },
            {
              label: t('general.business_group'),
              name: 'deptGroup',
              defaultValue: '',
              inputConfig: { ...inputConfig, inputProps: { maxLength: 10 } },
            },
            {
              label: t('general.department_name'),
              name: 'dept',
              defaultValue: '',
              inputConfig: { ...inputConfig, inputProps: { maxLength: 10 } },
            },
            {
              label: t('general.position'),
              name: 'position',
              defaultValue: '',
              inputConfig: { ...inputConfig, inputProps: { maxLength: 20 } },
            },
          ]}
          flexDirection="row"
          submitComponent={() => null}
          resetComponent={() => null}
        />
        {success && (
          <Stack
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              background: '#00000080',
              color: theme => theme.palette.other.white.main,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Stack direction="row" alignItems="center">
              <Typography variant="h5" mr={1}>
                {t('messages.user_created')}
              </Typography>
              <Check
                color="white"
                sx={{
                  background: theme => theme.palette.success.main,
                  borderRadius: '50%',
                  padding: 0.5,
                }}
              />
            </Stack>
            <MuiButton
              color="white"
              variant="text"
              sx={{ textDecoration: 'underline', background: 'transparent !important' }}
              disableRipple
              onClick={() => {
                onRemove?.(id)
              }}
            >
              {t('general.click_remove')}
            </MuiButton>
          </Stack>
        )}
      </Box>
    )
  },
)

export default CreateRegisteredUserCard
